import Button from 'devextreme-react/button';
import DateBox from 'devextreme-react/date-box';
import LoadIndicator from 'devextreme-react/load-indicator';
import SelectBox from 'devextreme-react/select-box';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import TextBox from 'devextreme-react/text-box';
import { GridFormPopup, opsGridFormPopupSizeKey } from '@jarvis/platform-ui';
import { useEffect, useState } from 'react';
import type { CreatedClientUser, UserGroupMembership } from '../../../../api/clientAccountsUsersAdmin';
import { GRID_STATE_KEY, buildUpn } from './hvgClientsColumns';
import { HvgClientGroupAccessPanel } from './HvgClientGroupAccessPanel';
import {
  HvgClientFormField,
  composeDisplayName,
  hvgClientFieldHint,
  hvgClientFormGrid2,
  hvgClientFormLayout,
  hvgClientFormPopupMinWidth,
  hvgClientFormPopupWidth,
  hvgClientUserFormPopupHeight,
  hvgClientUserFormPopupMinHeight
} from './hvgClientFormLayout';
import './hvgClientUserFormPopup.css';

export type UserFormState = {
  givenName: string;
  surname: string;
  accountName: string;
  domain: string;
  mail: string;
  department: string;
  jobTitle: string;
  company: string;
  employeeType: string;
  employeeHireDate: string | null;
  officeLocation: string;
  businessPhone: string;
  groupIds: string[];
};

export function emptyUserForm(): UserFormState {
  return {
    givenName: '',
    surname: '',
    accountName: '',
    domain: '',
    mail: '',
    department: '',
    jobTitle: '',
    company: '',
    employeeType: '',
    employeeHireDate: null,
    officeLocation: '',
    businessPhone: '',
    groupIds: []
  };
}

export type CreateProgressState = {
  title: string;
  detail: string;
  error: string | null;
  done: boolean;
};

type HvgClientUserFormPopupProps = {
  visible: boolean;
  mode: 'create' | 'edit' | 'view';
  editingId: string | null;
  form: UserFormState;
  formError: string | null;
  saving: boolean;
  createProgress: CreateProgressState | null;
  createdUser: CreatedClientUser | null;
  groupOptions: UserGroupMembership[];
  domainOptions: string[];
  domainsError: string | null;
  loadingGroups: boolean;
  loadingDomains: boolean;
  activeTab: number;
  hasP2License: boolean;
  p2AvailableUnits: number | null;
  p2LicenseBusy: boolean;
  onActiveTabChange: (index: number) => void;
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
  onSwitchToEdit?: () => void;
  onAssignP2?: () => void;
  onRemoveP2?: () => void;
  onFormChange: (patch: Partial<UserFormState>) => void;
};

export function HvgClientUserFormPopup({
  visible,
  mode,
  editingId,
  form,
  formError,
  saving,
  createProgress,
  createdUser,
  groupOptions,
  domainOptions,
  domainsError,
  loadingGroups,
  loadingDomains,
  activeTab,
  hasP2License,
  p2AvailableUnits,
  p2LicenseBusy,
  onActiveTabChange,
  onClose,
  onSave,
  onDelete,
  onSwitchToEdit,
  onAssignP2,
  onRemoveP2,
  onFormChange
}: HvgClientUserFormPopupProps) {
  const [passwordCopied, setPasswordCopied] = useState(false);
  const isCreate = mode === 'create' || editingId == null;
  const isView = mode === 'view';
  const readOnly = isView;
  const computedUpn = buildUpn(form.accountName, form.domain);
  const readOnlyUpn =
    form.accountName && form.domain ? `${form.accountName}@${form.domain}` : computedUpn;
  const computedDisplayName = composeDisplayName(form.givenName, form.surname);

  useEffect(() => {
    if (!visible) setPasswordCopied(false);
  }, [visible]);

  const copyPassword = async () => {
    if (!createdUser?.tempPassword) return;
    try {
      await navigator.clipboard.writeText(createdUser.tempPassword);
      setPasswordCopied(true);
    } catch {
      setPasswordCopied(false);
    }
  };

  const title = createProgress
    ? 'Creating Entra user'
    : isCreate
      ? 'Add Entra user'
      : isView
        ? 'View Entra user'
        : 'Edit Entra user';

  const popupHeight = isCreate
    ? hvgClientUserFormPopupHeight.create
    : hvgClientUserFormPopupHeight.edit;
  const popupMinHeight = isCreate
    ? hvgClientUserFormPopupMinHeight.create
    : hvgClientUserFormPopupMinHeight.edit;

  const hireDateValue = form.employeeHireDate ? new Date(`${form.employeeHireDate}T00:00:00`) : null;
  const formatDateOnly = (value: Date): string => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const canAssignP2 = !hasP2License && (p2AvailableUnits == null || p2AvailableUnits > 0);
  const canRemoveP2 = hasP2License;

  const licensePanel =
    !isCreate && !createProgress ? (
      <section className="hvg-user-form-card hvg-user-form-card--accent">
        <div className="hvg-user-form-card__heading">
          <span className="dx-icon dx-icon-key" aria-hidden="true" />
          <div>
            <div className="hvg-user-form-card__title">Entra ID P2 license</div>
            <p>Assign or remove the prepaid P2 license for this user.</p>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 14, lineHeight: 1.45 }}>
            <div>
              Status:{' '}
              <strong>{hasP2License ? 'Has Entra ID P2' : 'Missing P2'}</strong>
            </div>
            <div style={{ ...hvgClientFieldHint, marginTop: 4 }}>
              {p2AvailableUnits == null
                ? 'Available prepaid seats: unknown'
                : `Available prepaid seats: ${p2AvailableUnits}`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              text={p2LicenseBusy ? 'Working…' : 'Assign P2'}
              type="default"
              stylingMode="contained"
              disabled={p2LicenseBusy || saving || !canAssignP2 || !onAssignP2}
              onClick={onAssignP2}
            />
            <Button
              text={p2LicenseBusy ? 'Working…' : 'Remove P2'}
              type="danger"
              stylingMode="outlined"
              disabled={p2LicenseBusy || saving || !canRemoveP2 || !onRemoveP2}
              onClick={onRemoveP2}
            />
          </div>
        </div>
      </section>
    ) : null;

  return (
    <GridFormPopup
      visible={visible}
      title={title}
      onClose={onClose}
      width={hvgClientFormPopupWidth}
      minWidth={hvgClientFormPopupMinWidth}
      preferredHeight={popupHeight}
      minHeight={popupMinHeight}
      sizeStorageKey={opsGridFormPopupSizeKey(GRID_STATE_KEY)}
      error={createProgress ? createProgress.error : formError}
      footer={
        createProgress ? (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
            {createProgress.done || createProgress.error ? (
              <Button text="Close" type="default" onClick={onClose} />
            ) : (
              <span style={{ fontSize: 13, color: 'var(--text-muted, #6b7280)' }}>
                Please wait — do not close this window.
              </span>
            )}
          </div>
        ) : isView ? (
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center'
            }}
          >
            <div>
              {onDelete ? (
                <Button text="Delete" type="danger" disabled={saving || p2LicenseBusy} onClick={onDelete} />
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button text="Close" disabled={saving || p2LicenseBusy} onClick={onClose} />
              {onSwitchToEdit ? (
                <Button
                  text="Edit"
                  type="default"
                  disabled={saving || p2LicenseBusy}
                  onClick={onSwitchToEdit}
                />
              ) : null}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center'
            }}
          >
            <div>
              {!isCreate && onDelete ? (
                <Button text="Delete" type="danger" disabled={saving || p2LicenseBusy} onClick={onDelete} />
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button text="Cancel" disabled={saving || p2LicenseBusy} onClick={onClose} />
              <Button
                text={saving ? 'Saving…' : 'Save'}
                type="default"
                disabled={saving || p2LicenseBusy || loadingGroups || (isCreate && loadingDomains)}
                onClick={onSave}
              />
            </div>
          </div>
        )
      }
    >
      {createProgress ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0', minHeight: 120 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {!createProgress.done && !createProgress.error ? (
              <LoadIndicator visible height={28} width={28} />
            ) : null}
            <div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{createProgress.title}</div>
              <div
                style={{
                  marginTop: 6,
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: 'var(--text-muted, #4b5563)'
                }}
              >
                {createProgress.detail}
              </div>
            </div>
          </div>
          {createdUser?.tempPassword ? (
            <div
              style={{
                marginTop: 8,
                padding: '10px 12px',
                borderRadius: 6,
                background: 'var(--ops-status-succeeded-bg, #dcfce7)',
                border: '1px solid var(--ops-status-succeeded-border, #86efac)'
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Temporary password</div>
              <div style={{ fontFamily: 'monospace', fontSize: 14, marginBottom: 8 }}>
                {createdUser.tempPassword}
              </div>
              <Button
                text={passwordCopied ? 'Copied' : 'Copy password'}
                stylingMode="outlined"
                type="default"
                onClick={() => void copyPassword()}
              />
              <div style={{ ...hvgClientFieldHint, marginTop: 8 }}>
                Share this password securely. The user must change it at first sign-in.
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div style={hvgClientFormLayout}>
          <TabPanel
            className="ops-grid-detail-tab-panel"
            deferRendering={false}
            animationEnabled={false}
            swipeEnabled={false}
            showNavButtons={false}
            selectedIndex={activeTab}
            onSelectedIndexChange={(index) => onActiveTabChange(index)}
          >
            <TabItem title="Account">
              <div className="hvg-user-tab">
                <div className="hvg-user-tab__intro">
                  <div>
                    <div className="hvg-user-tab__title">Account identity</div>
                    <p>Set the person’s name, contact email, and Entra sign-in identity.</p>
                  </div>
                  <span className="hvg-user-tab__status">2 required fields</span>
                </div>

                <section className="hvg-user-form-card">
                  <div className="hvg-user-form-card__heading">
                    <span className="dx-icon dx-icon-user" aria-hidden="true" />
                    <div>
                      <div className="hvg-user-form-card__title">Person</div>
                      <p>Display name is generated automatically from first and last name.</p>
                    </div>
                  </div>
                  <div style={hvgClientFormGrid2}>
                    <HvgClientFormField label="First name *">
                      <TextBox
                        value={form.givenName}
                        placeholder="First name"
                        readOnly={readOnly}
                        onValueChanged={(e) => onFormChange({ givenName: e.value ?? '' })}
                        width="100%"
                      />
                    </HvgClientFormField>
                    <HvgClientFormField label="Last name *">
                      <TextBox
                        value={form.surname}
                        placeholder="Last name"
                        readOnly={readOnly}
                        onValueChanged={(e) => onFormChange({ surname: e.value ?? '' })}
                        width="100%"
                      />
                    </HvgClientFormField>
                    <HvgClientFormField label="Display name" span="full">
                      <TextBox value={computedDisplayName || 'Generated from first and last name'} readOnly width="100%" />
                    </HvgClientFormField>
                  </div>
                </section>

                <section className="hvg-user-form-card">
                  <div className="hvg-user-form-card__heading">
                    <span className="dx-icon dx-icon-email" aria-hidden="true" />
                    <div>
                      <div className="hvg-user-form-card__title">Contact &amp; sign-in</div>
                      <p>The email can differ from the user principal name used to sign in.</p>
                    </div>
                  </div>
                  <div style={hvgClientFormGrid2}>
                    <HvgClientFormField label="Email" hint="Optional — separate from UPN.">
                      <TextBox
                        value={form.mail}
                        mode="email"
                        placeholder="name@example.com"
                        readOnly={readOnly}
                        onValueChanged={(e) => onFormChange({ mail: e.value ?? '' })}
                        width="100%"
                      />
                    </HvgClientFormField>
                    {isCreate ? (
                      <HvgClientFormField label="Account name *">
                        <TextBox
                          value={form.accountName}
                          placeholder="jsmith"
                          onValueChanged={(e) => onFormChange({ accountName: e.value ?? '' })}
                          width="100%"
                        />
                      </HvgClientFormField>
                    ) : null}
                    {isCreate ? (
                      <>
                        <HvgClientFormField
                          label={`Domain *${loadingDomains ? ' (loading…)' : ''}`}
                          hint={
                            domainsError
                              ? `Could not load verified domains (${domainsError}). Type a verified domain manually.`
                              : undefined
                          }
                        >
                          <SelectBox
                            items={domainOptions}
                            value={form.domain || null}
                            searchEnabled
                            acceptCustomValue
                            disabled={loadingDomains}
                            placeholder="pharmasee.me"
                            width="100%"
                            onValueChanged={(e) => onFormChange({ domain: String(e.value ?? '').trim() })}
                            onCustomItemCreating={(e) => {
                              const text = String(e.text ?? '').trim().toLowerCase();
                              e.customItem = text;
                            }}
                          />
                        </HvgClientFormField>
                        <HvgClientFormField label="User principal name (UPN)">
                          <TextBox value={computedUpn || 'Generated from account and domain'} readOnly width="100%" />
                        </HvgClientFormField>
                      </>
                    ) : (
                      <HvgClientFormField
                        label="User principal name (UPN)"
                        hint="UPN cannot be changed after create."
                      >
                        <TextBox value={readOnlyUpn} readOnly width="100%" />
                      </HvgClientFormField>
                    )}
                  </div>
                </section>

                {licensePanel}
              </div>
            </TabItem>
            <TabItem title="Profile">
              <div className="hvg-user-tab">
                <div className="hvg-user-tab__intro">
                  <div>
                    <div className="hvg-user-tab__title">Client profile</div>
                    <p>Capture the client relationship, organization details, and business contact information.</p>
                  </div>
                  <span className="hvg-user-tab__status hvg-user-tab__status--optional">Optional</span>
                </div>

                <div className="hvg-user-card-stack">
                  <section className="hvg-user-form-card hvg-user-form-card--accent">
                    <div className="hvg-user-form-card__heading">
                      <span className="dx-icon dx-icon-card" aria-hidden="true" />
                      <div>
                        <div className="hvg-user-form-card__title">Client relationship</div>
                        <p>How and when this person joined the client account.</p>
                      </div>
                    </div>
                    <div style={hvgClientFormGrid2}>
                      <HvgClientFormField label="Client type">
                        <TextBox
                          value={form.employeeType}
                          placeholder="e.g. Primary contact"
                          readOnly={readOnly}
                          onValueChanged={(e) => onFormChange({ employeeType: e.value ?? '' })}
                          width="100%"
                        />
                      </HvgClientFormField>
                      <HvgClientFormField label="Client onboard date">
                        <DateBox
                          value={hireDateValue}
                          type="date"
                          displayFormat="yyyy-MM-dd"
                          showClearButton={!readOnly}
                          readOnly={readOnly}
                          width="100%"
                          onValueChanged={(e) => {
                            if (readOnly) return;
                            const next =
                              e.value instanceof Date && !Number.isNaN(e.value.getTime())
                                ? formatDateOnly(e.value)
                                : null;
                            onFormChange({ employeeHireDate: next });
                          }}
                        />
                      </HvgClientFormField>
                    </div>
                  </section>

                  <section className="hvg-user-form-card">
                    <div className="hvg-user-form-card__heading">
                      <span className="dx-icon dx-icon-home" aria-hidden="true" />
                      <div>
                        <div className="hvg-user-form-card__title">Organization</div>
                        <p>Where this person works and their role.</p>
                      </div>
                    </div>
                    <div style={hvgClientFormGrid2}>
                      <HvgClientFormField label="Company">
                        <TextBox
                          value={form.company}
                          readOnly={readOnly}
                          onValueChanged={(e) => onFormChange({ company: e.value ?? '' })}
                          width="100%"
                        />
                      </HvgClientFormField>
                      <HvgClientFormField label="Department">
                        <TextBox
                          value={form.department}
                          readOnly={readOnly}
                          onValueChanged={(e) => onFormChange({ department: e.value ?? '' })}
                          width="100%"
                        />
                      </HvgClientFormField>
                      <HvgClientFormField label="Job title" span="full">
                        <TextBox
                          value={form.jobTitle}
                          readOnly={readOnly}
                          onValueChanged={(e) => onFormChange({ jobTitle: e.value ?? '' })}
                          width="100%"
                        />
                      </HvgClientFormField>
                    </div>
                  </section>

                  <section className="hvg-user-form-card">
                    <div className="hvg-user-form-card__heading">
                      <span className="dx-icon dx-icon-tel" aria-hidden="true" />
                      <div>
                        <div className="hvg-user-form-card__title">Business contact</div>
                        <p>Primary workplace and phone details.</p>
                      </div>
                    </div>
                    <div style={hvgClientFormGrid2}>
                      <HvgClientFormField label="Office location">
                        <TextBox
                          value={form.officeLocation}
                          placeholder="Office or region"
                          readOnly={readOnly}
                          onValueChanged={(e) => onFormChange({ officeLocation: e.value ?? '' })}
                          width="100%"
                        />
                      </HvgClientFormField>
                      <HvgClientFormField label="Business phone">
                        <TextBox
                          value={form.businessPhone}
                          mode="tel"
                          placeholder="+1 555 010 1234"
                          readOnly={readOnly}
                          onValueChanged={(e) => onFormChange({ businessPhone: e.value ?? '' })}
                          width="100%"
                        />
                      </HvgClientFormField>
                    </div>
                  </section>
                </div>
              </div>
            </TabItem>
            <TabItem title="Access">
              <HvgClientGroupAccessPanel
                groupOptions={groupOptions}
                selectedGroupIds={form.groupIds}
                loading={loadingGroups}
                readOnly={readOnly}
                onChange={(groupIds) => onFormChange({ groupIds })}
              />
            </TabItem>
          </TabPanel>
        </div>
      )}
    </GridFormPopup>
  );
}
